import { useState, useTransition } from "react";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Input } from "~/components/input";
import { useAuth } from "~/contexts/auth";
import type { CustomerSearchResult } from "~/types";

import { CustomerPicker } from "./CustomerPicker";

export function AttachOrgToCustomerForm() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const { setIsAuthorized } = useAuth();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerSearchResult | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    const customerId = selectedCustomer.id;

    const formData = new FormData(e.currentTarget);
    const orgId = formData.get("org_id") as string;
    const orgName = formData.get("org_name") as string;

    startTransition(() => {
      (async () => {
        try {
          const res = await api.attachOrgToCustomer(customerId, orgId, orgName);
          setSuccess(res.message);
          setError(null);
        } catch (err: any) {
          setSuccess(null);
          if (err.status === 401) setIsAuthorized(false);
          setError(err.message ?? "Failed to attach org to customer");
        }
      })();
    });
  };

  return (
    <form method="post" className="space-y-4" onSubmit={handleSubmit}>
      {error ? <p className="text-red-500">{error}</p> : null}
      {success ? <p className="text-green-800">{success}</p> : null}
      <CustomerPicker
        id="customer_id"
        disabled={isPending}
        value={selectedCustomer}
        onChange={setSelectedCustomer}
      />
      <Input
        type="text"
        name="org_id"
        disabled={isPending}
        placeholder="org_id (from namespaces)"
        required
      />
      <Input
        type="text"
        name="org_name"
        disabled={isPending}
        placeholder="org name"
        required
      />
      <Button
        className="rounded bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
        type="submit"
        disabled={isPending || !selectedCustomer}
      >
        {isPending ? "Attaching..." : "Attach org"}
      </Button>
    </form>
  );
}
